import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { LifeBuoy, Send, Loader2, Search, MessageCircle, User as UserIcon, Shield } from "lucide-react";
import { useIsAdmin, useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_MS = 6000;

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const same = d.toDateString() === today.toDateString();
  return same
    ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }) + " " +
      d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

// ══════════════════════════════════════════════════════════════════
// Chat pane (shared between user & admin view)
// ══════════════════════════════════════════════════════════════════
function ChatPane({ thread, isAdmin, currentUserId, onRead }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    if (!thread?.id) return;
    try {
      const { data } = await axios.get(
        `${API}/support/threads/${thread.id}/messages`, { withCredentials: true },
      );
      setMessages(Array.isArray(data) ? data : []);
      // mark as read on load
      await axios.post(`${API}/support/threads/${thread.id}/read`, {}, { withCredentials: true }).catch(() => {});
      onRead?.();
    } finally { setLoading(false); }
  }, [thread?.id, onRead]);

  useEffect(() => {
    setLoading(true);
    load();
    const iv = setInterval(load, POLL_MS);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/support/threads/${thread.id}/messages`,
        { text: t },
        { withCredentials: true },
      );
      setMessages(prev => [...prev, data]);
      setText("");
    } finally { setSending(false); }
  };

  if (!thread) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-[#0A0C10] text-[#A0AEC0] text-sm gap-2">
        <MessageCircle className="w-8 h-8 text-[#1F242D]" />
        <p>Seleziona un thread per iniziare la conversazione</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-[#0A0C10] min-h-0">
      {/* Thread header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.04] bg-[#12161C] flex-shrink-0">
        <div className="w-10 h-10 rounded-full flex items-center justify-center border-2 border-[#00E5FF] bg-[#00E5FF]/10 flex-shrink-0">
          {isAdmin ? <UserIcon className="w-4 h-4 text-[#00E5FF]" /> : <Shield className="w-4 h-4 text-[#00E5FF]" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-white font-semibold truncate leading-tight">
            {isAdmin ? thread.user_name : "Team Support Moonstone"}
          </p>
          <p className="text-[10px] text-[#A0AEC0] tracking-widest uppercase truncate mt-0.5">
            {isAdmin ? `${thread.user_email} · ${thread.client_name}` : "Rispondiamo entro 24 ore lavorative"}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-[#A0AEC0]">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Caricamento…
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle className="w-8 h-8 text-[#1F242D] mx-auto mb-2" />
            <p className="text-xs text-[#A0AEC0]">Nessun messaggio ancora — scrivi qualcosa per iniziare!</p>
          </div>
        ) : messages.map(m => {
          const mine = m.sender_id === currentUserId;
          const isSupport = m.sender === "admin";
          const align = mine ? "justify-end" : "justify-start";
          const bubbleClass = mine
            ? "bg-[#00E5FF] text-black rounded-lg rounded-br-none"
            : isSupport
              ? "bg-[#A78BFA]/15 border border-[#A78BFA]/40 text-white rounded-lg rounded-bl-none"
              : "bg-[#1A1D24] border border-white/[0.04] text-white rounded-lg rounded-bl-none";
          return (
            <div key={m.id} data-testid="chat-msg" className={`flex ${align}`}>
              <div className={`max-w-[80%] px-4 py-2.5 ${bubbleClass}`}>
                {!mine && (
                  <p className={`text-[9px] tracking-widest uppercase font-bold mb-1 ${isSupport ? "text-[#A78BFA]" : "text-[#00E5FF]"}`}>
                    {isSupport ? "Support" : m.sender_name}
                  </p>
                )}
                <p className="text-sm leading-snug whitespace-pre-wrap break-words">{m.text}</p>
                <p className={`text-[9px] mt-1 ${mine ? "text-black/60" : "text-[#A0AEC0]"} text-right`}>
                  {formatTime(m.created_at)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <form onSubmit={send} className="flex items-center gap-2 px-4 py-3 border-t border-white/[0.04] bg-[#12161C] flex-shrink-0">
        <input
          data-testid="chat-input"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Scrivi un messaggio…"
          className="flex-1 bg-[#050505] border border-white/[0.04] text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] placeholder:text-[#6B7280]"
        />
        <button
          data-testid="chat-send-button"
          type="submit"
          disabled={!text.trim() || sending}
          className="flex items-center gap-2 bg-[#00E5FF] text-black font-semibold px-4 py-2 text-xs hover:bg-[#00B3CC] transition-colors disabled:opacity-40"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Invia
        </button>
      </form>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Main page
// ══════════════════════════════════════════════════════════════════
export default function CustomerSupportPage() {
  const isAdmin = useIsAdmin();
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Load user thread OR admin thread list
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (isAdmin) {
          const { data } = await axios.get(`${API}/support/threads`, { withCredentials: true });
          setThreads(Array.isArray(data) ? data : []);
          if (data?.length) setThread(data[0]);
        } else {
          const { data } = await axios.get(`${API}/support/thread`, { withCredentials: true });
          setThread(data?.thread || null);
        }
      } finally { setLoading(false); }
    })();
  }, [isAdmin]);

  // Poll thread list for admins to keep unread counters in sync
  useEffect(() => {
    if (!isAdmin) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/support/threads`, { withCredentials: true });
        setThreads(Array.isArray(data) ? data : []);
      } catch (err) { console.warn("[CustomerSupport] threads poll failed:", err); }
    }, POLL_MS);
    return () => clearInterval(iv);
  }, [isAdmin]);

  const filteredThreads = threads.filter(t =>
    !search.trim() ||
    [t.user_name, t.user_email, t.client_name].some(v => (v || "").toLowerCase().includes(search.toLowerCase())),
  );

  const onReadCurrent = useCallback(() => {
    if (!isAdmin) return;
    setThreads(prev => prev.map(t => t.id === thread?.id ? { ...t, unread_admin: 0 } : t));
  }, [isAdmin, thread?.id]);

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3" style={{ fontFamily: "Chivo, sans-serif" }}>
            <LifeBuoy className="w-6 h-6 text-[#00E5FF]" />
            Customer Support
          </h1>
          <p className="text-xs text-[#A0AEC0] mt-1 tracking-widest uppercase">
            {isAdmin
              ? `${threads.length} thread aperti · rispondi ai clienti in tempo reale`
              : "Chat diretta con il team Moonstone Analytics"}
          </p>
        </div>
      </div>

      <div className="flex bg-[#0D0F12] border border-white/[0.04] rounded-xl overflow-hidden" style={{ height: "72vh", minHeight: 500 }}>
        {/* Admin: sidebar with threads */}
        {isAdmin && (
          <aside className="w-80 border-r border-white/[0.04] flex flex-col bg-[#0A0C10] min-w-0">
            <div className="p-3 border-b border-white/[0.04]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                <input
                  data-testid="thread-search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Cerca cliente, email…"
                  className="w-full bg-[#050505] border border-white/[0.04] text-white pl-10 pr-3 py-2 text-xs focus:outline-none focus:border-[#00E5FF]"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-xs text-[#A0AEC0]">Caricamento…</div>
              ) : filteredThreads.length === 0 ? (
                <div className="p-4 text-xs text-[#A0AEC0] italic">Nessun thread aperto.</div>
              ) : filteredThreads.map(t => {
                const active = thread?.id === t.id;
                const unread = t.unread_admin > 0;
                return (
                  <button
                    key={t.id}
                    data-testid="thread-row"
                    onClick={() => setThread(t)}
                    className={`w-full text-left px-4 py-3 border-b border-white/[0.04] transition-colors ${
                      active ? "bg-[#00E5FF]/10 border-l-2 border-l-[#00E5FF]" : "hover:bg-[#1A1D24]"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[#1F242D] flex-shrink-0">
                        <UserIcon className="w-3.5 h-3.5 text-[#00E5FF]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 justify-between">
                          <p className={`text-xs font-medium truncate ${active ? "text-[#00E5FF]" : "text-white"}`}>{t.user_name}</p>
                          {unread && (
                            <span data-testid="unread-badge" className="text-[9px] font-bold tabular-nums bg-[#FF3B30] text-white px-1.5 py-0.5 rounded-full flex-shrink-0">
                              {t.unread_admin}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-[#A0AEC0] truncate">{t.client_name || t.user_email}</p>
                        {t.last_message_preview && (
                          <p className="text-[10px] text-[#6B7280] mt-1 truncate">
                            {t.last_message_sender === "admin" ? "Tu: " : ""}
                            {t.last_message_preview}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>
        )}

        {/* Chat pane */}
        <ChatPane
          thread={thread}
          isAdmin={isAdmin}
          currentUserId={user?.id}
          onRead={onReadCurrent}
        />
      </div>
    </div>
  );
}
